import { useEffect, useState } from "react";
import {
  Drawer,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Toolbar,
  Typography,
  Avatar,
  Stack,
  CircularProgress,
  Collapse,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { Dashboard, ExpandLess, ExpandMore, HelpOutline, Settings, Logout, Business, People, Extension, Link as LinkIcon, Close, Security, HealthAndSafety, Store, Assignment, AttachMoney, Description } from "@mui/icons-material";
import * as MuiIcons from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import api from '../services/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { useUserPreferences } from '../hooks/useUserPreferences.js';

const minDrawerWidth = 240;
const maxDrawerWidth = 500;

export default function MenuLateral({ open = true, onToggle, anchor = "right" }) {
  const [usuario, setUsuario] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [adminOpen, setAdminOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(240);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, logout } = useAuth();
  const { userPhoto } = useUserPreferences();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    if (
      location.pathname.startsWith('/empresas') ||
      location.pathname.startsWith('/usuarios') ||
      location.pathname.startsWith('/modulos') ||
      location.pathname.startsWith('/auditoria')
    ) {
      setAdminOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("usuario");
    if (stored) {
      try {
        const user = JSON.parse(stored);
        setUsuario({
          nome_completo: user.st_nome,
          email: user.st_email
        });
      } catch (error) {
        console.error('Erro ao carregar usuário:', error);
      }
    }
  }, []);

  useEffect(() => {
    const savedWidth = localStorage.getItem('drawerWidth');
    if (savedWidth) {
      setDrawerWidth(parseInt(savedWidth, 10));
    }
  }, []);

  useEffect(() => {
    const carregarMenu = async () => {
      try {
        const resMenu = await api.get('/menu');
        let itensMenuBanco = [];

        if (resMenu.data.status === 'sucesso') {
          itensMenuBanco = resMenu.data.dados || [];
        }

        const pathsEstaticos = [
          '/cockpit',
          '/dashboard',
          '/empresas',
          '/usuarios',
          '/modulos',
          '/auditoria',
          '/preferencias',
          '/representantes',
          '/atribuirrepresentante',
          '/planos',
          '/parceiros',
          '/parceiros',
          '/contratos',
          '/minhas-comissoes',
          '/gestao-comissoes' // Add to static paths filter if I add it manually below
        ];

        const itensMenuFiltrados = itensMenuBanco.filter(item => {
          if (item.path && pathsEstaticos.includes(item.path.toLowerCase())) {
            return false;
          }
          const nomesEstaticos = [
            'Cockpit',
            'Dashboard',
            'Dados da Empresa',
            'Usuários',
            'Módulos',
            'Auditoria',
            'Logs de auditoria',
            'Preferências',
            'Configurações'
          ];
          if (nomesEstaticos.includes(item.nome)) {
            return false;
          }
          return true;
        });

        itensMenuFiltrados.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
        setMenuItems(itensMenuFiltrados);
      } catch (err) {
        console.error('Erro ao carregar menu:', err);
        setMenuItems([]);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      carregarMenu();
    }
  }, [user]);

  const handleToggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Function Declarations for Hoisting safety
  function verificarPerfil(perfisPermitidos) {
    if (!user) {
      return false;
    }
    const perfilUsuario = user.perfil || user.en_perfil || '';
    return perfisPermitidos.some(perfil =>
      perfilUsuario.toLowerCase() === perfil.toLowerCase()
    );
  }

  function getMuiIconByName(nomeIcone) {
    if (!nomeIcone) return <HelpOutline sx={{ color: "#20ac9c" }} />;

    const mapeamentoEspecial = {
      'restore': 'Restore',
      'cockpit': 'Dashboard',
      'dashboard': 'Dashboard',
      'users': 'People',
      'user': 'Person',
      'company': 'Business',
      'service': 'Build',
      'schedule': 'Schedule',
      'finance': 'AttachMoney',
      'report': 'Assessment',
      'settings': 'Settings'
    };

    if (mapeamentoEspecial[nomeIcone]) {
      const nomeMapeado = mapeamentoEspecial[nomeIcone];
      if (Object.prototype.hasOwnProperty.call(MuiIcons, nomeMapeado)) {
        const Icon = MuiIcons[nomeMapeado];
        return <Icon sx={{ color: "#20ac9c" }} />;
      }
    }

    const variantes = [
      nomeIcone,
      `${nomeIcone}Outlined`,
      `${nomeIcone}Rounded`,
      `${nomeIcone}TwoTone`,
      `${nomeIcone}Sharp`,
    ];

    for (let nome of variantes) {
      if (Object.prototype.hasOwnProperty.call(MuiIcons, nome)) {
        const Icon = MuiIcons[nome];
        return <Icon sx={{ color: "#20ac9c" }} />;
      }
    }

    return <HelpOutline sx={{ color: "#20ac9c" }} />;
  }

  function renderMenuItem(item, level = 0) {
    const hasSubmenus = item.submenus && item.submenus.length > 0;
    const isExpanded = expandedItems[item.id] || false;
    const isSelected = location.pathname === item.path;

    return (
      <Box key={item.id}>
        <ListItemButton
          onClick={() => {
            if (hasSubmenus) {
              handleToggleExpand(item.id);
            } else if (item.path) {
              navigate(item.path);
            }
          }}
          sx={{
            pl: 2 + level * 2,
            borderRadius: 1,
            mb: 0.5,
            backgroundColor: isSelected ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(32, 172, 156, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            {getMuiIconByName(item.icone)}
          </ListItemIcon>
          <ListItemText
            primary={item.nome}
            sx={{
              '& .MuiTypography-root': {
                fontSize: '1rem',
                fontWeight: isSelected ? 600 : 500,
              }
            }}
          />
          {hasSubmenus && (
            isExpanded ? <ExpandLess /> : <ExpandMore />
          )}
        </ListItemButton>
        {hasSubmenus && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ pl: 2 }}>
              {item.submenus.map(subItem => renderMenuItem(subItem, level + 1))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  }

  if (!usuario) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor={anchor}
      open={open}
      onClose={isMobile ? onToggle : undefined}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        zIndex: (theme) => theme.zIndex.drawer,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
          top: { xs: '56px', sm: '64px' },
          background: theme.palette.mode === 'dark'
            ? "linear-gradient(180deg, #1a1a1a 0%, #121212 100%)"
            : "#f5f5f5",
          borderLeft: anchor === "right" ? "1px solid rgba(0,0,0,0.12)" : "none",
          borderRight: anchor === "left" ? "1px solid rgba(0,0,0,0.12)" : "none",
          boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.1)",
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          ...(open ? {} : { width: 0 }),
        },
      }}
    >
      {/* Topo - Apenas botão de fechar no mobile */}
      {isMobile && onToggle && (
        <Box>
          <Toolbar sx={{ minHeight: "56px", p: 1, position: 'relative', justifyContent: 'flex-end' }}>
            <IconButton
              onClick={onToggle}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              aria-label="fechar menu"
            >
              <Close />
            </IconButton>
          </Toolbar>
          <Divider />
        </Box>
      )}

      {/* Lista de Menu com Scroll - Alinhada no topo */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        pt: 2,
      }}>
        {/* Dashboard fixo */}
        <ListItemButton
          onClick={() => navigate("/cockpit")}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            backgroundColor: location.pathname === '/cockpit' ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(32, 172, 156, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Dashboard sx={{ color: "#20ac9c" }} />
          </ListItemIcon>
          <ListItemText
            primary="Cockpit"
            sx={{
              '& .MuiTypography-root': {
                fontSize: '1rem',
                fontWeight: location.pathname === '/cockpit' ? 600 : 500,
              }
            }}
          />
        </ListItemButton>

        {/* Beneficiários */}
        <ListItemButton
          onClick={() => navigate("/beneficiarios")}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            backgroundColor: location.pathname.startsWith('/beneficiarios') ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(32, 172, 156, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <People sx={{ color: "#20ac9c" }} />
          </ListItemIcon>
          <ListItemText
            primary="Beneficiários"
            sx={{
              '& .MuiTypography-root': {
                fontSize: '1rem',
                fontWeight: location.pathname.startsWith('/beneficiarios') ? 600 : 500,
              }
            }}
          />
        </ListItemButton>

        {/* Representantes */}
        <ListItemButton
          onClick={() => navigate("/representantes")}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            backgroundColor: location.pathname.startsWith('/representantes') ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(32, 172, 156, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <MuiIcons.Work sx={{ color: "#20ac9c" }} />
          </ListItemIcon>
          <ListItemText
            primary="Representantes"
            sx={{
              '& .MuiTypography-root': {
                fontSize: '1rem',
                fontWeight: location.pathname.startsWith('/representantes') ? 600 : 500,
              }
            }}
          />
        </ListItemButton>

        {/* Atribuir Representante */}
        <ListItemButton
          onClick={() => navigate("/atribuirrepresentante")}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            backgroundColor: location.pathname.startsWith('/atribuirrepresentante') ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(32, 172, 156, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LinkIcon sx={{ color: "#20ac9c" }} />
          </ListItemIcon>
          <ListItemText
            primary="Atribuir Representante"
            sx={{
              '& .MuiTypography-root': {
                fontSize: '1rem',
                fontWeight: location.pathname.startsWith('/atribuirrepresentante') ? 600 : 500,
              }
            }}
          />
        </ListItemButton>

        {/* Planos */}
        <ListItemButton
          onClick={() => navigate("/planos")}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            backgroundColor: location.pathname.startsWith('/planos') ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(32, 172, 156, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <HealthAndSafety sx={{ color: "#20ac9c" }} />
          </ListItemIcon>
          <ListItemText
            primary="Planos de Saúde"
            sx={{
              '& .MuiTypography-root': {
                fontSize: '1rem',
                fontWeight: location.pathname.startsWith('/planos') ? 600 : 500,
              }
            }}
          />
        </ListItemButton>

        {/* Parceiros / Rede Credenciada */}
        <ListItemButton
          onClick={() => navigate("/parceiros")}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            backgroundColor: location.pathname.startsWith('/parceiros') ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(32, 172, 156, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Store sx={{ color: "#20ac9c" }} />
          </ListItemIcon>
          <ListItemText
            primary="Rede Credenciada"
            sx={{
              '& .MuiTypography-root': {
                fontSize: '1rem',
                fontWeight: location.pathname.startsWith('/parceiros') ? 600 : 500,
              }
            }}
          />
        </ListItemButton>

        {/* Contratos / Vendas */}
        <ListItemButton
          onClick={() => navigate("/contratos")}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            backgroundColor: location.pathname.startsWith('/contratos') ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(32, 172, 156, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Assignment sx={{ color: "#20ac9c" }} />
          </ListItemIcon>
          <ListItemText
            primary="Contratos"
            sx={{
              '& .MuiTypography-root': {
                fontSize: '1rem',
                fontWeight: location.pathname.startsWith('/contratos') ? 600 : 500,
              }
            }}
          />
        </ListItemButton>

        {/* Minhas Comissões (Representantes) */}
        {verificarPerfil(['representante', 'superusuario', 'gerente']) && (
          <ListItemButton
            onClick={() => navigate("/minhascomissoes")}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              backgroundColor: location.pathname === '/minhascomissoes' ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(32, 172, 156, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <AttachMoney sx={{ color: "#20ac9c" }} />
            </ListItemIcon>
            <ListItemText
              primary="Comissões"
              sx={{
                '& .MuiTypography-root': {
                  fontSize: '1rem',
                  fontWeight: location.pathname === '/minhascomissoes' ? 600 : 500,
                }
              }}
            />
          </ListItemButton>
        )}

        {/* Grupo Administração */}
        {verificarPerfil(['superusuario', 'gerente', 'administrador']) && (
          <Box>
            <ListItemButton
              onClick={() => setAdminOpen(!adminOpen)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                backgroundColor: (location.pathname.startsWith('/empresas') ||
                  location.pathname.startsWith('/usuarios') ||
                  location.pathname.startsWith('/modulos') ||
                  location.pathname.startsWith('/auditoria'))
                  ? 'rgba(32, 172, 156, 0.1)'
                  : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(32, 172, 156, 0.08)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Settings sx={{ color: "#20ac9c" }} />
              </ListItemIcon>
              <ListItemText
                primary="Administração"
                sx={{
                  '& .MuiTypography-root': {
                    fontSize: '1rem',
                    fontWeight: 500,
                  }
                }}
              />
              {adminOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 2 }}>
                {/* Empresas - para superusuários e gerentes */}
                {verificarPerfil(['superusuario', 'gerente']) && (
                  <ListItemButton
                    onClick={() => navigate("/empresas")}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      pl: 4,
                      backgroundColor: location.pathname.startsWith('/empresas') ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(32, 172, 156, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Business sx={{ color: "#20ac9c" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Dados da Empresa"
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.95rem',
                          fontWeight: location.pathname.startsWith('/empresas') ? 600 : 400,
                        }
                      }}
                    />
                  </ListItemButton>
                )}

                {/* Usuários - para superusuários, gerentes e administradores */}
                {verificarPerfil(['superusuario', 'gerente', 'administrador']) && (
                  <ListItemButton
                    onClick={() => navigate("/usuarios")}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      pl: 4,
                      backgroundColor: location.pathname === '/usuarios' ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(32, 172, 156, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <People sx={{ color: "#20ac9c" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Usuários"
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.95rem',
                          fontWeight: location.pathname === '/usuarios' ? 600 : 400,
                        }
                      }}
                    />
                  </ListItemButton>
                )}

                {/* Módulos - para superusuários e gerentes */}
                {verificarPerfil(['superusuario', 'gerente']) && (
                  <ListItemButton
                    onClick={() => navigate("/modulos")}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      pl: 4,
                      backgroundColor: location.pathname.startsWith('/modulos') ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(32, 172, 156, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Extension sx={{ color: "#20ac9c" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Módulos"
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.95rem',
                          fontWeight: location.pathname.startsWith('/modulos') ? 600 : 400,
                        }
                      }}
                    />
                  </ListItemButton>
                )}

                {/* Auditoria - para superusuários e gerentes */}
                {verificarPerfil(['superusuario', 'gerente']) && (
                  <ListItemButton
                    onClick={() => navigate("/auditoria")}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      pl: 4,
                      backgroundColor: location.pathname === '/auditoria' ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(32, 172, 156, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Security sx={{ color: "#20ac9c" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Logs de auditoria"
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.95rem',
                          fontWeight: location.pathname === '/auditoria' ? 600 : 400,
                        }
                      }}
                    />
                  </ListItemButton>
                )}

                {/* Pagamento Comissões - para superusuários, gerentes e administradores */}
                {verificarPerfil(['superusuario', 'gerente', 'administrador']) && (
                  <ListItemButton
                    onClick={() => navigate("/gestaocomissoes")}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      pl: 4,
                      backgroundColor: location.pathname === '/gestaocomissoes' ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(32, 172, 156, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AttachMoney sx={{ color: "#20ac9c" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Pagamento Comissões"
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.95rem',
                          fontWeight: location.pathname === '/gestaocomissoes' ? 600 : 400,
                        }
                      }}
                    />
                  </ListItemButton>
                )}

                {/* Ajustar Contrato - para superusuários, gerentes e administradores */}
                {verificarPerfil(['superusuario', 'gerente', 'administrador']) && (
                  <ListItemButton
                    onClick={() => navigate("/ajustar-contrato")}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      pl: 4,
                      backgroundColor: location.pathname === '/ajustar-contrato' ? 'rgba(32, 172, 156, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(32, 172, 156, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Description sx={{ color: "#20ac9c" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Ajustar Contrato"
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: '0.95rem',
                          fontWeight: location.pathname === '/ajustar-contrato' ? 600 : 400,
                        }
                      }}
                    />
                  </ListItemButton>
                )}
              </Box>
            </Collapse>
          </Box>
        )
        }

        {/* Menu dinâmico */}
        {
          loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            menuItems.map(item => renderMenuItem(item))
          )
        }
      </Box >

      {/* Rodapé */}
      < Box sx={{
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'
      }}>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
              mb: 0.5
            }}
          >
            {time.toLocaleTimeString('pt-BR', { hour12: false })}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              mb: 1,
              textTransform: 'capitalize'
            }}
          >
            {time.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            GestCard v1.0.0
          </Typography>
        </Box>
      </Box >
    </Drawer >
  );
}
